/**
 * fabStack — RW-2269 (F9).
 *
 * Three fixed buttons live in the bottom-right corner and, until now, only two
 * of them knew about each other. On a 390px phone the feed's create-post FAB
 * (56px at right 20 / bottom 88) sat under the bug-report launcher (40px at
 * right 32 / bottom 90, max z-index), which is entirely inside its box — so a
 * tap on "+" opened "Report an Issue". The quick-actions FAB (56px at right 24
 * / bottom 84 on mobile) overlapped both.
 *
 * The fix reuses RW-1486's clearance channel rather than inventing a second
 * one: the create FAB registers itself with `useFabObstruction`, and the two
 * app-level buttons lift above it exactly as they already lift above Ask
 * Rêve's pinned composer. These are the numbers all three render from, so the
 * geometry test below them cannot pass while the components disagree.
 *
 * Offsets are px from the viewport bottom / right, ignoring the iOS
 * home-indicator inset (`--reve-safe-bottom`), which moves the whole stack
 * together.
 */

/** The feed's create-post FAB: above the mobile nav (72px) with 16px of air. */
export const CREATE_FAB = { size: 56, right: 20, bottom: 72 + 16 };

/** Quick actions (App.js): the base offsets it has always used. */
export const QUICK_FAB = { size: 56, right: 24, bottomMobile: 84, bottomDesktop: 24, liftPad: 12 };

/** Bug report (App.js): 40px, centred on the quick-actions FAB's 52px axis. */
export const BUG_FAB = { size: 40, right: 32 };

/** Air between two stacked buttons. */
export const FAB_GAP = 10;

/**
 * FLOW's mobile tab bar (RW-1303, NinjaFlow/index.js) — the module's ONLY
 * intra-module navigation on a phone, since FlowRail renders nothing there.
 *
 * RW-2323: it is full-width and pinned above the app's MobileNav, which puts
 * its right-hand "More" tab directly under the two corner buttons. It never
 * registered itself on RW-1486's clearance channel, so the FABs did not lift
 * and covered it at z-index 900 vs 57 — burying the tab that reaches Meeting
 * agenda, Submit week, Export week, History, Consolidated and Team FLOW.
 *
 * `bottom` matches MOBILE_NAV_CLEARANCE and `height` the tab buttons'
 * minHeight plus the 1px top border. NinjaFlow renders from these, so the
 * geometry test cannot pass while the component disagrees.
 */
export const FLOW_TAB_BAR = { bottom: 64, height: 55 };

/** How far up from the viewport bottom FLOW's tab bar reaches. */
export const FLOW_TAB_BAR_REACH = FLOW_TAB_BAR.bottom + FLOW_TAB_BAR.height;

/** How far up from the viewport bottom the create FAB reaches. */
export const CREATE_FAB_REACH = CREATE_FAB.bottom + CREATE_FAB.size;

/** Quick-actions offset for a given registered obstruction height. */
export function quickFabBottom(lift, mobile) {
  const base = mobile ? QUICK_FAB.bottomMobile : QUICK_FAB.bottomDesktop;
  return Math.max(base, lift ? lift + QUICK_FAB.liftPad : 0);
}

/**
 * Bug-launcher offset: always one gap above the quick-actions FAB.
 *
 * On desktop this reproduces the 90px it has always used (24 + 56 + 10). On
 * mobile the old hard-coded 90 sat INSIDE the quick-actions FAB, which the
 * mobile nav had already pushed to 84 — the same class of collision as the
 * create FAB's, and the reason this is derived now rather than written twice.
 */
export function bugFabBottom(lift, mobile) {
  return quickFabBottom(lift, mobile) + QUICK_FAB.size + FAB_GAP;
}

/**
 * RW-2953: where the bug launcher rests when nothing is in its way. Stacked a
 * gap above the quick-actions FAB while that is on screen; when quick actions
 * steps aside (a modal is up, or the phone feed's own "+" replaces it) the
 * launcher drops into its slot instead of floating over an empty gap.
 */
export function bugFabHomeBottom(lift, mobile, quickShown = true) {
  return quickShown ? bugFabBottom(lift, mobile) : quickFabBottom(lift, mobile);
}

/**
 * RW-3408: how far up from the viewport bottom the resting corner stack reaches
 * (quick actions, the bug launcher above it, and a gap) — what a page must leave
 * clear at its bottom so its last toggles, buttons and numbers can scroll out
 * from under them. App.js publishes it, less the mobile nav the shell already
 * pads, as `--reve-fab-clearance`; `.reve-main` pads by it.
 */
export function fabStackReach(mobile) {
  return bugFabBottom(0, mobile) + BUG_FAB.size + FAB_GAP;
}

/** The launcher's minimum distance from the viewport's top and side edges. */
export const BUG_FAB_EDGE = 12;

const grow = (r, by) => ({ left: r.left - by, right: r.right + by, top: r.top - by, bottom: r.bottom + by });

/**
 * RW-2953 (Clint, 2026-10-06): the bug launcher "should sit above any modal
 * but not over another button". It keeps its max z-index so a bug can be
 * reported from inside a modal; this decides WHERE it sits so it never covers
 * one of that modal's buttons.
 *
 * `avoid` is the viewport rects of the buttons it must not cover (BugReporter
 * passes the open modal's buttons). From its home slot it climbs, one button
 * at a time, to a gap above whatever it would cover; if the right-hand column
 * has no free spot below the top edge it tries the same climb on the left. If
 * neither side has room it stays home — on screen, rather than off it.
 *
 * Pure, so the no-overlap promise is testable without a layout engine.
 *
 * @returns {{ side: 'right'|'left', offset: number, bottom: number, rect: Object }}
 *   `offset` is px from that side; `bottom` px from the viewport bottom.
 */
export function bugLauncherSpot({ width, height, mobile = false, lift = 0, quickShown = true, avoid = [] }) {
  const size = BUG_FAB.size;
  const home = bugFabHomeBottom(lift, mobile, quickShown);
  const rectAt = (side, offset, bottom) => {
    const left = side === 'right' ? width - offset - size : offset;
    return { left, right: left + size, top: height - bottom - size, bottom: height - bottom };
  };
  const solid = avoid.filter((a) => a && a.right > a.left && a.bottom > a.top);
  for (const [side, offset] of [['right', BUG_FAB.right], ['left', BUG_FAB_EDGE]]) {
    let bottom = home;
    // Each pass climbs strictly above the button it hit, so a button is hit
    // at most once: avoid.length + 1 passes always settle.
    for (let i = 0; i <= solid.length; i += 1) {
      const rect = rectAt(side, offset, bottom);
      if (rect.top < BUG_FAB_EDGE) break; // climbed off the top: try the other side
      const hit = solid.find((a) => rectsIntersect(grow(rect, FAB_GAP / 2), a));
      if (!hit) return { side, offset, bottom, rect };
      bottom = Math.max(bottom, height - hit.top + FAB_GAP);
    }
  }
  return { side: 'right', offset: BUG_FAB.right, bottom: home, rect: rectAt('right', BUG_FAB.right, home) };
}

/**
 * Where the three buttons land, as viewport rects, so a test can check that
 * none of them covers another.
 *
 * @param {Object} opts
 * @param {number} opts.width - viewport width
 * @param {number} opts.height - viewport height
 * @param {boolean} opts.mobile
 * @param {boolean} opts.createFab - is the feed's create FAB on screen
 */
export function fabStackRects({ width, height, mobile = false, createFab = false, flowTabs = false, registerFlowTabs = true, modalOpen = false }) {
  // RW-2953: on a phone the feed's create "+" replaces the quick-actions "+"
  // (one create entry, not two stacked coral buttons), and while a modal is up
  // neither page-level button renders at all.
  const createShown = createFab && !modalOpen;
  const quickShown = !modalOpen && !(mobile && createShown);
  // RW-2323: FLOW's tab bar is an obstruction like the create FAB — when it is
  // on screen the corner buttons must lift above it. `registerFlowTabs: false`
  // reproduces the pre-fix state, where it was on screen but registered
  // nothing.
  const lift = Math.max(
    createShown ? CREATE_FAB_REACH : 0,
    flowTabs && registerFlowTabs ? FLOW_TAB_BAR_REACH : 0,
  );
  const box = (spec, bottom) => ({
    left: width - spec.right - spec.size,
    right: width - spec.right,
    top: height - bottom - spec.size,
    bottom: height - bottom,
  });
  const rects = { bug: box(BUG_FAB, bugFabHomeBottom(lift, mobile, quickShown)) };
  if (quickShown) rects.quick = box(QUICK_FAB, quickFabBottom(lift, mobile));
  if (createShown) rects.create = box(CREATE_FAB, CREATE_FAB.bottom);
  if (flowTabs) {
    // Full-bleed, so its box spans the viewport width.
    rects.flowTabs = {
      left: 0,
      right: width,
      top: height - FLOW_TAB_BAR_REACH,
      bottom: height - FLOW_TAB_BAR.bottom,
    };
  }
  return rects;
}

export function rectsIntersect(a, b) {
  if (!a || !b) return false;
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

/**
 * Where the toast stack starts, so a toast never lands on a button.
 *
 * RW-2271 (F11), found by the RW-2269 staging walk: ToastContext renders its
 * stack at a hard-coded `bottom: 24 / right: 24` with `pointerEvents: 'auto'`
 * on each toast. On a 390px phone that box sits directly on the create FAB and
 * the mobile bottom nav — so for the seconds a toast is up, "+" and the nav
 * are unreachable, and the toast that says "Post shared" is what stops you
 * sharing the next one. Same family as F9's three-button collision, and fixed
 * the same way: derived from this file rather than written a fourth time.
 *
 * Desktop keeps the 24 it has always used — nothing overlaps there, because the
 * create FAB is a mobile affordance and the quick-actions FAB sits at 24 with
 * the toast column beside it, not over it. On mobile the stack clears the
 * whole FAB column.
 */
export function toastBottom(lift, mobile) {
  if (!mobile) return 24;
  return bugFabBottom(lift, mobile) + BUG_FAB.size + FAB_GAP;
}
